import React, { FC, useState, useEffect, Fragment } from 'react';
import { Grid, Button, Box, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LinkedUser, LinkedClinic } from 'models/order';
import LinkedDentists from './linked-dentists.component';
import LinkedClinics from './linked-clinics.component';
import ParticipantsList from './participants-list.component';
import { emailRegExp } from 'config/regex';
import { User } from 'models/user';
import { Organization } from 'models/organization';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import DHDialog from '../dh-dialog/dh-dialog.component';
import InfoText from '../info-text.component';

type ManageParticipantsDialogProps = {
    onParticipantsChanged?: (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => void;
    orderLinkedDentists?: LinkedUser[];
    orderLinkedClinics?: LinkedClinic[];
    orderCustomParticipantEmails?: string[];
    onClose: () => void;
    open: boolean;
};
const ManageParticipantsDialog: FC<ManageParticipantsDialogProps> = ({
    onParticipantsChanged,
    orderLinkedDentists,
    orderLinkedClinics,
    orderCustomParticipantEmails,
    onClose,
    open,
}: ManageParticipantsDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices', 'user-settings']);
    const [linkedDentists, setLinkedDentists] = useState<LinkedUser[]>([]);
    const [linkedClinics, setLinkedClinics] = useState<LinkedClinic[]>([]);
    const [customEmails, setCustomEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(false);

    useEffect(() => {
        if (orderLinkedDentists) {
            setLinkedDentists(orderLinkedDentists);
        }
        if (orderLinkedClinics) {
            setLinkedClinics(orderLinkedClinics);
        }
        if (orderCustomParticipantEmails) {
            setCustomEmails(orderCustomParticipantEmails);
            setIsValidEmail(true);
        }
    }, [orderLinkedDentists, orderLinkedClinics, orderCustomParticipantEmails]);

    const handleAddDentists = (linkedDentists: LinkedUser[]) => {
        setLinkedDentists(linkedDentists);
    };

    const handleAddClinics = (linkedClinics: LinkedClinic[]) => {
        setLinkedClinics(linkedClinics);
    };

    const validateEmail = (value: string) => {
        const email = emailRegExp.test(String(value).toLowerCase());
        setIsValidEmail(email);
        return email;
    };

    const handleToError = (err: Error | null) => {
        setIsValidEmail(!err);
    };

    const handleEmailInput = (value: string[]) => {
        setCustomEmails(value);
    };

    const handleRemoveLinkedDentist = (dentist: User) => {
        if (linkedDentists) {
            setLinkedDentists(linkedDentists.filter((linkedDentist) => linkedDentist.user._id !== dentist._id));
        }
    };

    const handleRemoveLinkedClinic = (clinic: Organization) => {
        if (linkedClinics) {
            setLinkedClinics(linkedClinics.filter((linkedClinic) => linkedClinic.clinic._id !== clinic._id));
        }
    };

    const handleClose = () => {
        onClose();
        // Prevent UI glitch when closing
        setTimeout(() => {
            setIsValidEmail(false);
            setLoading(false);
        }, 500);
    };

    const handleAddParticipants = async () => {
        if (onParticipantsChanged) {
            onParticipantsChanged(linkedDentists, linkedClinics, customEmails);
        }
        handleClose();
    };

    return (
        <Fragment>
            <DHDialog
                title={translate('orders:MANAGE_PARTICIPANTS')}
                fullWidth
                maxWidth="lg"
                open={open}
                onClose={handleClose}
                actions={
                    <Button
                        variant="contained"
                        disabled={customEmails.length > 0 && !isValidEmail}
                        onClick={handleAddParticipants}
                        data-test="manage-participants.save-btn"
                    >
                        {translate('SAVE')}
                    </Button>
                }
            >
                {loading ? <DelayedLinearProgress data-test="manage-participants.loading" /> : undefined}
                <Box mb={2}>
                    <Alert severity="info">{translate('orders:PARTICIPANT_INFO')}</Alert>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <LinkedDentists orderLinkedDentists={linkedDentists} onAddDentists={handleAddDentists} />
                        </Box>
                        <Box mt={2}>
                            <LinkedClinics orderLinkedClinics={linkedClinics} onAddClinics={handleAddClinics} />
                        </Box>
                        <Box mt={2}>
                            <ChipInput
                                label={translate('user-settings:EMAIL_ADDRESSES')}
                                placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                createChipOnBlur
                                options={[]}
                                chipValidator={validateEmail}
                                onError={handleToError}
                                defaultValue={customEmails}
                                onValueChange={(value: string[]) => handleEmailInput(value)}
                                onDelete={(value: string[]) => handleEmailInput(value)}
                                dataTest="manage-participants.email-chip-input"
                            />
                            <Box mt={0.5}>
                                <InfoText text={translate('invoices:EMAIL_PLACEHOLDER')} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box p={1} sx={{ height: '100%', backgroundColor: 'grey.50' }}>
                            <ParticipantsList
                                showRemoveButton={true}
                                linkedDentists={linkedDentists}
                                linkedClinics={linkedClinics}
                                customEmails={customEmails}
                                onRemoveDentist={handleRemoveLinkedDentist}
                                onRemoveClinic={handleRemoveLinkedClinic}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DHDialog>
        </Fragment>
    );
};

export default ManageParticipantsDialog;
